"use client";

import { IconDownload } from "@tabler/icons-react";
import { gql } from "graphql-request";
import { useMemo, useState } from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { OrgProjectColorDot } from "@/components/org/org-project-chip";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useGraphqlQuery } from "@/lib/graphql/useGraphql";
import {
  formatCostUsd,
  type UsageLedgerFilter,
  useUsageBuckets,
  useUsageLedger,
  useUsageSummary,
} from "@/lib/usage/usage.hooks";

const compactNumber = (value: number) =>
  new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(value);

const formatDate = (iso: string) =>
  new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(iso));

const chartConfig = {
  spend: { label: "Spend", color: "var(--chart-1)" },
} satisfies ChartConfig;

const PERIOD_30D = { preset: "last_30d" } as const;

const usageProjectsDocument = gql`
  query UsagePageProjects {
    me {
      org {
        projects {
          id
          name
          color
        }
      }
    }
  }
`;

type UsageProjectsQuery = {
  me: {
    org: {
      projects: Array<{ id: string; name: string; color: string | null }>;
    } | null;
  } | null;
};

export default function AnalyticsPage() {
  const [modelFilter, setModelFilter] = useState<string>("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");

  const handleProjectFilterChange = (value: string) => {
    setProjectFilter(value);
    // Model chips are derived from the active project scope; reset so a model
    // only present in the previous project cannot empty the new ledger view.
    setModelFilter("all");
  };

  const projectsQuery = useGraphqlQuery<UsageProjectsQuery>({
    queryKey: ["usage", "projects"],
    document: usageProjectsDocument,
  });
  const projects = useMemo(() => projectsQuery.data?.me?.org?.projects ?? [], [projectsQuery.data]);
  const projectById = useMemo(() => new Map(projects.map((project) => [project.id, project])), [projects]);
  const selectedProjectId = projectFilter === "all" ? null : projectFilter;

  const activeFilter: UsageLedgerFilter | null = useMemo(() => {
    const filter: UsageLedgerFilter = {};
    if (modelFilter !== "all") {
      filter.model = modelFilter;
    }
    if (selectedProjectId) {
      filter.project_id = selectedProjectId;
    }
    return Object.keys(filter).length > 0 ? filter : null;
  }, [modelFilter, selectedProjectId]);

  const modelFilterOnly: UsageLedgerFilter | null = selectedProjectId ? { project_id: selectedProjectId } : null;

  const { data: summaryData, isLoading: summaryLoading } = useUsageSummary(PERIOD_30D, {
    projectId: selectedProjectId,
  });
  const { data: dailyData, isLoading: dailyLoading } = useUsageBuckets({ count: 30 }, { projectId: selectedProjectId });
  // Ledger scoped only by project (not model) — drives the model filter buttons.
  const { data: unfilteredLedgerData } = useUsageLedger(200, null, modelFilterOnly);
  // Filtered ledger — drives the table rows.
  const { data: ledgerData, isLoading: ledgerLoading } = useUsageLedger(200, null, activeFilter);

  const summary = summaryData?.usage_summary;
  const dailyBuckets = useMemo(() => dailyData?.usage_buckets ?? [], [dailyData]);
  const modelRows = useMemo(() => summaryData?.usage_summary?.by_model ?? [], [summaryData]);
  const ledgerEvents = useMemo(() => ledgerData?.usage_ledger?.events ?? [], [ledgerData]);

  const modelNames = useMemo(
    () => Array.from(new Set((unfilteredLedgerData?.usage_ledger?.events ?? []).map((e) => e.model))).sort(),
    [unfilteredLedgerData],
  );

  const dailySpendLine = useMemo(
    () =>
      dailyBuckets.map((b) => ({
        label: new Intl.DateTimeFormat("en-US", { month: "numeric", day: "numeric" }).format(
          new Date(`${b.date}T00:00:00`),
        ),
        spend: Number(b.cost_cents) / 100,
      })),
    [dailyBuckets],
  );

  const isLoading = summaryLoading || dailyLoading || ledgerLoading;

  const handleExportCsv = () => {
    const header = ["Date", "Project", "Model", "Input tokens", "Output tokens", "Cost (USD)", "API key"].join(",");
    const csv = [
      header,
      ...ledgerEvents.map((e) =>
        [
          `"${formatDate(e.occurred_at)}"`,
          `"${e.project_name ?? "—"}"`,
          `"${e.model}"`,
          e.input_tokens,
          e.output_tokens,
          `"${formatCostUsd(e.cost_cents)}"`,
          `"${e.api_key_label ?? "—"}"`,
        ].join(","),
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "usage.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="@container/main flex flex-1 flex-col gap-2">
      <div className="flex flex-col gap-4 md:gap-6">
        <PageHeader
          title="Usage"
          description="Monitor API spend, token consumption, and per-model usage across your organization."
          actions={
            <>
              {projects.length > 0 ? (
                <Select value={projectFilter} onValueChange={(value) => handleProjectFilterChange(String(value))}>
                  <SelectTrigger variant="filter" className="w-full sm:w-auto sm:min-w-40">
                    <SelectValue placeholder="View by Project">
                      <span className="inline-flex items-center gap-1">
                        <span className="text-gray-500">View by</span>
                        <span>
                          {projectFilter === "all" ? "Project" : (projectById.get(projectFilter)?.name ?? "Project")}
                        </span>
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All projects</SelectItem>
                    {projects.map((project) => (
                      <SelectItem
                        key={project.id}
                        value={project.id}
                        endAdornment={<OrgProjectColorDot color={project.color} />}
                      >
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
              <Button variant="outline" className="shrink-0" onClick={handleExportCsv} disabled={isLoading}>
                <IconDownload className="mr-2 size-4" />
                Export CSV
              </Button>
            </>
          }
        />

        {/* Summary cards */}
        <div>
          <h2 className="mb-3 text-sm font-medium text-muted-foreground">Summary (last 30 days)</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Estimated spend</CardTitle>
              </CardHeader>
              <CardContent>
                {summaryLoading ? (
                  <p className="text-muted-foreground text-sm">Loading...</p>
                ) : (
                  <p className="text-2xl font-semibold">{formatCostUsd(summary?.estimated_cost_cents ?? 0)}</p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total input tokens</CardTitle>
              </CardHeader>
              <CardContent>
                {summaryLoading ? (
                  <p className="text-muted-foreground text-sm">Loading...</p>
                ) : (
                  <p className="text-2xl font-semibold">{compactNumber(Number(summary?.input_tokens ?? 0))}</p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total output tokens</CardTitle>
              </CardHeader>
              <CardContent>
                {summaryLoading ? (
                  <p className="text-muted-foreground text-sm">Loading...</p>
                ) : (
                  <p className="text-2xl font-semibold">{compactNumber(Number(summary?.output_tokens ?? 0))}</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Per-model breakdown */}
        <div>
          <h2 className="mb-3 text-sm font-medium text-muted-foreground">Per-model breakdown</h2>
          {summaryLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : modelRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No usage yet.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {[...modelRows]
                .sort((a, b) => Number(b.cost_cents) - Number(a.cost_cents))
                .map((row) => (
                  <Card key={row.model}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base font-semibold">{row.model}</CardTitle>
                      <CardDescription>
                        {row.requests} {Number(row.requests) === 1 ? "request" : "requests"}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xl font-semibold">{formatCostUsd(row.cost_cents)}</p>
                    </CardContent>
                  </Card>
                ))}
            </div>
          )}
        </div>

        {/* Daily spend chart */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Daily spend</CardTitle>
              <CardDescription>Last 30 days.</CardDescription>
            </CardHeader>
            <CardContent className="pl-0">
              {dailyLoading ? (
                <p className="py-10 text-center text-sm text-muted-foreground">Loading...</p>
              ) : (
                <ChartContainer config={chartConfig} className="aspect-auto h-[240px] w-full">
                  <LineChart data={dailySpendLine} margin={{ left: 12, right: 12 }}>
                    <CartesianGrid vertical={false} />
                    <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} minTickGap={16} />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      tickFormatter={(v) => `$${Number(v).toFixed(0)}`}
                    />
                    <ChartTooltip
                      cursor={{ strokeDasharray: "4 4" }}
                      content={<ChartTooltipContent formatter={(v) => [`$${Number(v).toFixed(2)}`, "Spend"]} />}
                    />
                    <Line type="monotone" dataKey="spend" stroke="var(--color-spend)" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ChartContainer>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Transaction log */}
        <div>
          <h2 className="mb-3 text-sm font-medium text-muted-foreground">Transaction log</h2>
          <div className="mb-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={modelFilter === "all" ? "secondary" : "outline"}
              onClick={() => setModelFilter("all")}
            >
              All
            </Button>
            {modelNames.map((name) => (
              <Button
                key={name}
                size="sm"
                variant={modelFilter === name ? "secondary" : "outline"}
                className="max-w-[200px] truncate"
                onClick={() => setModelFilter(name)}
              >
                {name}
              </Button>
            ))}
          </div>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead className="text-right">Input tokens</TableHead>
                  <TableHead className="text-right">Output tokens</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead>API key</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledgerLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                      Loading usage...
                    </TableCell>
                  </TableRow>
                ) : ledgerEvents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                      No usage records yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  ledgerEvents.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="whitespace-nowrap">{formatDate(e.occurred_at)}</TableCell>
                      <TableCell className="max-w-[180px] truncate">{e.project_name ?? "—"}</TableCell>
                      <TableCell className="max-w-[220px] truncate font-mono text-xs">{e.model}</TableCell>
                      <TableCell className="text-right tabular-nums">{compactNumber(e.input_tokens)}</TableCell>
                      <TableCell className="text-right tabular-nums">{compactNumber(e.output_tokens)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCostUsd(e.cost_cents)}</TableCell>
                      <TableCell className="max-w-[180px] truncate font-mono text-xs">
                        {e.api_key_label ?? "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
